import { Form, FormikProps } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { ErrorsSummedUpRender } from "Components/Form/ErrorsSummedUp";
import { Input } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { PostNumberAndCity } from "Components/Form/PostNumberAndCity";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { nextStep } from "Store/steps/stepsSlice";
import { useProductTexts } from "Store/texts/hooks";
import { mapValidationErrors } from "Utils/formUtils";
import { getError } from "Utils/productUtils";
import { AdditionalBuildings } from "../../AdditionalBuildings";
import { ConstructionYear } from "../../FormComponentsHouseProducts/ConstructionYear";
import { CABIN_INTRO_STEP } from "../cabinSteps";
import { GrossAreaQuestion } from "./FormComponents/GrossAreaQuestion";
import { CabinProductIntroFormValues } from "./cabinProductIntroInterfaces";
import { validationSchema } from "./cabinProductIntroValidation";

export const CabinProductIntro = (): React.ReactElement => {
  const dispatch = useDispatch();
  const initialFormState = useFormState<CabinProductIntroFormValues>(
    CABIN_INTRO_STEP.id,
    initialFormValues,
  );
  const [additionalBuildings, setAdditionalBuildings] = useState(false);
  const [additionalBuildingsError, setAdditionalBuildingsError] =
    useState(false);
  const [
    {
      formText: { aboutCabin },
      details,
    },
    lang,
  ] = useProductTexts(ProductBackendName.CABIN);

  const { getText } = useTexts();
  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const updateFormValues = (
    formProps: FormikProps<CabinProductIntroFormValues>,
  ): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: CABIN_INTRO_STEP.id,
        formState: { ...formProps.values },
      }),
    );
  };
  const handleSubmit = (formProps: CabinProductIntroFormValues): void => {
    if (additionalBuildings) {
      setAdditionalBuildingsError(() => true);
      return;
    }
    setAdditionalBuildingsError(() => false);
    dispatch(
      updatePriceCalculationFormState({
        stepId: CABIN_INTRO_STEP.id,
        formState: {
          ...formProps,
        },
      }),
    );
    dispatch(nextStep());
  };
  useEffect(() => {
    if (!additionalBuildings) {
      setAdditionalBuildingsError(() => false);
    }
  }, [additionalBuildings]);

  return (
    <ProductIntroTile
      productBackendName={ProductBackendName.CABIN}
      validationSchema={validationSchema()}
      initialValues={{
        ...initialFormState,
        otherBuildings: initialFormState.otherBuildings
          ? initialFormState.otherBuildings
          : [],
      }}
      onSubmit={handleSubmit}
    >
      {(formProps: FormikProps<CabinProductIntroFormValues>) => {
        return (
          <Form>
            <PostNumberAndCity
              formProps={formProps}
              label={getText("cabinform.label.postnumber")}
            />
            <ConstructionYear
              formProps={formProps}
              label={getText("cabinform.label.constructionyear")}
              hint={getText("cabinform.hint.constructionyear")}
              errorMessage={getText(
                "cabin.validation.constructionyearoldcabin",
              )}
            />
            <GrossAreaQuestion
              formProps={formProps}
              productText={aboutCabin}
              lang={lang}
            />
            <Input
              name="livingArea"
              id="livingArea"
              hint={aboutCabin.livingArea.hint[lang]}
              label={aboutCabin.livingArea.label[lang]}
              error={getError("livingArea", formProps)}
              type="number"
              large
              inputMode={InputMode.NUMERIC}
            />
            <AdditionalBuildings
              onClick={() => {
                updateFormValues(formProps);
                setAdditionalBuildings(true);
              }}
              formProps={formProps}
              additionalBuildings={additionalBuildings}
              setAdditionalBuildings={setAdditionalBuildings}
              updateFormValues={updateFormValues}
              product={ProductBackendName.CABIN}
              stepId={CABIN_INTRO_STEP.id}
              error={additionalBuildingsError}
            />

            <PartnerProductIntroForms />
            <MarketingConsent />

            <ErrorsSummedUpRender
              formProps={formProps}
              errors={mapValidationErrors(formProps.errors, details, lang)}
              isVisible
            />
            <ScrollToFirstError />
            <NavigationButtons
              hidePreviousButton
              nextButtonOnClick={() => {
                if (additionalBuildings) {
                  setAdditionalBuildingsError(() => true);
                }
              }}
              isLoading={priceCalculationResult.isLoading}
            />
          </Form>
        );
      }}
    </ProductIntroTile>
  );
};

const initialFormValues = {
  postNumber: undefined,
  city: undefined,
  validPostNumber: undefined,
  constructionYear: undefined,
  grossArea: undefined,
  livingArea: undefined,
  otherBuildings: undefined,
};
